import { Plugin } from 'vue';
import Vue3TouchEvents, { type Vue3TouchEventsOptions } from 'vue3-touch-events';

import { I18nPlugin, StashOptionImages, StashProvideState } from '../types/misc';
import locale from './locale';
// this is valid syntax per the TS docs, but prettier throws an error expecting a comma after `type`
import DialogsPlugin, { type DialogsPluginOptions } from './plugins/DialogsPlugin';
import MenusPlugin, { type MenusPluginOptions } from './plugins/MenusPlugin';
import ModalsPlugin, { type ModalsPluginOptions } from './plugins/ModalsPlugin';
import ToastsPlugin, { type ToastsPluginOptions } from './plugins/ToastsPlugin';
import { overridePersistentStorage, persistentStorage } from './storage';

export interface StashPluginOptions {
  i18n?: I18nPlugin;
  storage?: {
    set: <T = unknown>(name: string, data: T, options?: { [key: string]: unknown }) => void;
    get: <T = unknown>(name: string, options?: { [key: string]: unknown }) => T;
  };
  staticPath?: string;
  images?: StashOptionImages;
  googleMapsApiKey?: string;
  menus?: false | MenusPluginOptions;
  modals?: false | ModalsPluginOptions;
  toasts?: false | ToastsPluginOptions;
  dialogs?: false | DialogsPluginOptions;
}

export default <Plugin>{
  install(
    app,
    {
      i18n = undefined,
      storage = undefined,
      staticPath = '/static',
      images = {
        provider: 'static',
      },
      googleMapsApiKey = undefined,
      menus,
      modals,
      toasts,
      dialogs,
    }: StashPluginOptions = {},
  ) {
    app.use<Vue3TouchEventsOptions>(Vue3TouchEvents, {});

    // locale
    if (i18n) {
      locale.use(i18n.locale);
      locale.i18n(i18n && i18n.t);
    }

    // persistent storage (localStorage)
    if (storage) {
      overridePersistentStorage(storage);
    }

    // expose stash options to the rest of your application
    app.provide<StashProvideState>('stashOptions', {
      i18n,
      images,
      storage,
      staticPath,
      googleMapsApiKey,
    });

    if (menus !== false) {
      app.use(MenusPlugin, menus);
    }

    if (modals !== false) {
      app.use(ModalsPlugin, modals);
    }

    if (toasts !== false) {
      app.use(ToastsPlugin, toasts);
    }

    if (dialogs !== false) {
      app.use(DialogsPlugin, dialogs);
    }
  },
};

export { persistentStorage };

// Re-export things that are useful for consumers
export * from '../types';
export * from './storage';

// Todo: Investigate tree-shaking issues to see if we can expose these. Theoritically
// should be no issue, however test framework startup time, specifically w/ Jest,
// seems to be the major blocker here.
// export * from './components';
// export * from './composables';
// export * from './constants';
// export * from './directives';
// export * from './locale';
// export * from './plugins';
// export { default as ToastsPlugin } from './plugins/ToastsPlugin';
